import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react'
import { useProgress } from '@/contexts/ProgressProvider'
import { useNavigation } from '@/contexts/NavigationProvider'
import { HowToPlayTutorial } from '@/components/screens/HowToPlayTutorial'

interface TutorialContextValue {
  openTutorial: () => void
}

const TutorialContext = createContext<TutorialContextValue | null>(null)

export function TutorialProvider({ children }: { children: ReactNode }) {
  const { save, update } = useProgress()
  const { screen } = useNavigation()
  const [open, setOpen] = useState(false)

  useEffect(() => {
    if (save.meta.tutorialSeen) return
    if (screen === 'boot') return
    setOpen(true)
  }, [save.meta.tutorialSeen, screen])

  const openTutorial = useCallback(() => setOpen(true), [])

  const close = useCallback(() => {
    setOpen(false)
    if (!save.meta.tutorialSeen) {
      update((s) => ({ ...s, meta: { ...s.meta, tutorialSeen: true } }))
    }
  }, [save.meta.tutorialSeen, update])

  return (
    <TutorialContext.Provider value={{ openTutorial }}>
      {children}
      <HowToPlayTutorial open={open} onClose={close} />
    </TutorialContext.Provider>
  )
}

export function useTutorial(): TutorialContextValue {
  const ctx = useContext(TutorialContext)
  if (!ctx) throw new Error('useTutorial must be used within TutorialProvider')
  return ctx
}
